import { useCreateMultiDayShift } from 'api/hooks/shift/use-create-multi-day-shift';
import { useCreateShift } from 'api/hooks/shift/use-create-shift';
import { useDuplicateShift } from 'api/hooks/shift/use-duplicate-shift';
import { useUpdateShift } from 'api/hooks/shift/use-update-shift';
import { shiftAlerts, useAlert } from 'shared/components/alerts';
import { invariant } from 'shared/utils/utils';
import { mapMultiDayFormDataToDto } from '../helpers/map-multi-day-form-data-to-dto';
import { mapFormDataToDto } from '../helpers/mappers';
import { WorkFormContext, WorkFormMode } from '../work-form.types';

type Props = {
  shiftId: string | undefined;
  defaultValues: Partial<WorkFormContext> | undefined;
  formMode: WorkFormMode;
  cancel: () => void;
  onSuccess: (shiftId: string) => void;
};

export const useWorkFormMutations = ({
  shiftId,
  formMode,
  defaultValues,
  cancel,
  onSuccess,
}: Props) => {
  const { alertSuccess, alertError } = useAlert();
  const createMutation = useCreateShift();
  const createMultiDayMutation = useCreateMultiDayShift();
  const updateMutation = useUpdateShift();
  const duplicateMutation = useDuplicateShift();

  const onSubmit = async (data: WorkFormContext) => {
    if (formMode === 'create' && data.createTab === 'multi-day') {
      const multiDayRequestData = mapMultiDayFormDataToDto(data, defaultValues);
      createMultiDayMutation.mutate(multiDayRequestData, {
        onSuccess: ([firstShiftId]) => {
          alertSuccess(shiftAlerts.success.created);
          cancel();
          onSuccess(firstShiftId);
        },
        onError: (e) => {
          alertError(e);
        },
      });
      return;
    }

    const requestData = mapFormDataToDto(data, defaultValues);
    switch (formMode) {
      case 'create':
        createMutation.mutateAsync(requestData, {
          onSuccess: ([firstShiftId]) => {
            alertSuccess(shiftAlerts.success.created);
            cancel();
            onSuccess(firstShiftId);
          },
          onError: (e) => {
            alertError(e);
          },
        });
        break;

      case 'edit':
        invariant(shiftId, 'Shift ID is required for edit mode');
        updateMutation.mutateAsync(
          { ...requestData, id: shiftId },
          {
            onSuccess: () => {
              alertSuccess(shiftAlerts.success.updated);
              cancel();
              onSuccess(shiftId);
            },
            onError: (e) => {
              alertError(e);
            },
          }
        );

        break;
      case 'duplicate':
        invariant(shiftId, 'Shift ID is required for duplicate mode');
        duplicateMutation.mutateAsync(
          { ...requestData, ancestorShiftId: shiftId },
          {
            onSuccess: ([firstShiftId]) => {
              alertSuccess(shiftAlerts.success.created);
              cancel();
              onSuccess(firstShiftId);
            },
            onError: (e) => {
              alertError(e);
            },
          }
        );
    }
  };
  return {
    onSubmit,
  };
};
